import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Typography } from '@/components/ui/typography'
import {
  getPriceAdjustmentCardTitle,
  getPriceAdjustmentSignedAmountLabel,
} from '@/lib/services/price-adjustments/priceAdjustments.summary'
import { cn } from '@/lib/utils'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { useState } from 'react'
import {
  formatActiveWindowEndDate,
  getAdjustmentActiveAfterBoundary,
  getCurrentCollectionOverrideGroups,
  getFallbackAdjustmentForCollection,
  getLatestStartedAdjustment,
  getUniqueCollections,
  priceAdjustmentIsPriority,
} from './-price-change-board'

function CollectionSubjectLabel({ collections }) {
  const [isHiddenCollectionListOpen, setIsHiddenCollectionListOpen] = useState(false)
  const [isHiddenCollectionListPinned, setIsHiddenCollectionListPinned] = useState(false)
  const uniqueCollections = getUniqueCollections(collections)
  const collectionCount = uniqueCollections.length

  if (collectionCount === 0) {
    return <>Selected collections</>
  }

  if (collectionCount === 1) {
    return <>{uniqueCollections[0].name} collection</>
  }

  if (collectionCount === 2) {
    return (
      <>
        {uniqueCollections[0].name} and {uniqueCollections[1].name} collections
      </>
    )
  }

  const hiddenCollections = uniqueCollections.slice(2)
  const hiddenCollectionCount = hiddenCollections.length
  const hiddenCollectionLabel = `and ${hiddenCollectionCount} more ${
    hiddenCollectionCount === 1 ? 'collection' : 'collections'
  }`

  const handleHiddenCollectionListOpenChange = (isOpen) => {
    setIsHiddenCollectionListOpen(isOpen)

    if (!isOpen) {
      setIsHiddenCollectionListPinned(false)
    }
  }

  const handleHiddenCollectionTriggerClick = (event) => {
    event.preventDefault()

    const nextPinnedState = !isHiddenCollectionListPinned

    setIsHiddenCollectionListPinned(nextPinnedState)
    setIsHiddenCollectionListOpen(nextPinnedState)
  }

  const handleHiddenCollectionTriggerMouseEnter = () => {
    if (!isHiddenCollectionListPinned) {
      setIsHiddenCollectionListOpen(true)
    }
  }

  const handleHiddenCollectionTriggerMouseLeave = () => {
    if (!isHiddenCollectionListPinned) {
      setIsHiddenCollectionListOpen(false)
    }
  }

  return (
    <>
      {uniqueCollections[0].name}, {uniqueCollections[1].name},{' '}
      <Popover
        open={isHiddenCollectionListOpen}
        onOpenChange={handleHiddenCollectionListOpenChange}
      >
        <PopoverTrigger asChild>
          <Button
            variant="link"
            size="inline"
            aria-label={hiddenCollectionLabel}
            aria-expanded={isHiddenCollectionListOpen}
            onClick={handleHiddenCollectionTriggerClick}
            onMouseEnter={handleHiddenCollectionTriggerMouseEnter}
            onMouseLeave={handleHiddenCollectionTriggerMouseLeave}
          >
            {hiddenCollectionLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent side="top" size="small" padding="compact" elevation="strong">
          <div className="space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              More collections
            </p>
            <ul className="space-y-1">
              {hiddenCollections.map((collection) => (
                <li key={collection.id} className="text-xs text-foreground">
                  {collection.name}
                </li>
              ))}
            </ul>
          </div>
        </PopoverContent>
      </Popover>
    </>
  )
}

const formatCurrentDetailWindowPhrase = (adjustment) => {
  const endDate = formatActiveWindowEndDate(adjustment?.active_window?.ends_at)

  return endDate ? `through ${endDate}` : 'with no automatic end'
}

const getFallbackSentenceContent = (
  fallbackAdjustment,
  { prefix = 'Then', subject = 'those products' } = {}
) => {
  if (!fallbackAdjustment) {
    return (
      <>
        {prefix} {subject} go back to regular product prices.
      </>
    )
  }

  return (
    <>
      {prefix} {subject} go back to{' '}
      <strong className="font-semibold text-foreground">
        {getPriceAdjustmentCardTitle(fallbackAdjustment)}
      </strong>{' '}
      ({getPriceAdjustmentSignedAmountLabel(fallbackAdjustment)}).
    </>
  )
}

function CurrentPricingDetailRow({
  adjustment = null,
  amountLabel,
  children,
  onHighlightAdjustments,
  title,
}) {
  const isDecrease = adjustment?.operation === 'decrease'
  const DirectionIcon = isDecrease ? ArrowDown : ArrowUp
  const adjustmentId = Number(adjustment?.id)

  const handleShowOnBoard = () => {
    if (!adjustmentId || !onHighlightAdjustments) {
      return
    }

    onHighlightAdjustments([adjustmentId])
  }

  return (
    <div className="rounded-xl bg-muted/25 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Typography variant="labelStrong" className="block">
              {title}
            </Typography>
            {priceAdjustmentIsPriority(adjustment) ? (
              <Badge variant="info" size="sm">
                Priority
              </Badge>
            ) : null}
          </div>
          <Typography variant="supporting">{children}</Typography>
        </div>
        <div
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg bg-background/70 px-3 py-2 text-sm font-semibold',
            adjustment
              ? isDecrease
                ? 'text-interaction'
                : 'text-status-success-foreground'
              : 'text-muted-foreground'
          )}
        >
          {adjustment ? <DirectionIcon className="size-3.5" aria-hidden="true" /> : null}
          {amountLabel}
        </div>
      </div>
      {adjustmentId ? (
        <Button variant="link" size="inline-xs" className="mt-3" onClick={handleShowOnBoard}>
          Show on board
        </Button>
      ) : null}
    </div>
  )
}

export function CurrentPricingDetailsDialog({
  activeAdjustments = [],
  isOpen = false,
  onHighlightAdjustments,
  onOpenChange,
}) {
  const activeBaseAdjustments = activeAdjustments.filter(
    (adjustment) => adjustment?.scope_type === 'global'
  )
  const activeBaseAdjustment = getLatestStartedAdjustment(activeBaseAdjustments)
  const activeOverrideAdjustments = activeAdjustments.filter(
    (adjustment) => adjustment?.scope_type === 'collections'
  )
  const overrideGroups = getCurrentCollectionOverrideGroups(activeOverrideAdjustments)
  const baseFallbackAdjustment = getAdjustmentActiveAfterBoundary(
    activeBaseAdjustments,
    activeBaseAdjustment
  )

  const handleHighlightAdjustments = (adjustmentIds) => {
    onOpenChange?.(false)
    onHighlightAdjustments?.(adjustmentIds)
  }

  const baseRowTitle =
    overrideGroups.length > 0 ? 'Everything outside those collections' : 'All products'
  const baseAmountLabel = activeBaseAdjustment
    ? getPriceAdjustmentSignedAmountLabel(activeBaseAdjustment)
    : 'Regular prices'

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        size="xlarge"
        viewport="scroll"
        className="max-h-[calc(100vh-3rem)]"
      >
        <DialogHeader>
          <DialogTitle>Current pricing details</DialogTitle>
        </DialogHeader>

        <div id="price-change-current-details" data-testid="price-change-current-details-modal">
          <Typography variant="supporting" italic className="mb-4">
            Prices are applied from top to bottom. If an item belongs to multiple collections, the
            first matching rule is used.
          </Typography>

          <div className="space-y-3">
            {overrideGroups.map(({ adjustment, collections }) => {
              const fallbackAdjustments = [
                ...new Map(
                  collections
                    .map((collection) =>
                      getFallbackAdjustmentForCollection(
                        collection.id,
                        adjustment,
                        activeOverrideAdjustments,
                        activeBaseAdjustments
                      )
                    )
                    .filter(Boolean)
                    .map((fallbackAdjustment) => [
                      Number(fallbackAdjustment.id),
                      fallbackAdjustment,
                    ])
                ).values(),
              ]
              const fallbackContent =
                fallbackAdjustments.length === 1 ? (
                  getFallbackSentenceContent(fallbackAdjustments[0])
                ) : adjustment?.active_window?.ends_at ? (
                  <>Then those products go back to the next matching price change.</>
                ) : null

              return (
                <CurrentPricingDetailRow
                  key={adjustment.id}
                  adjustment={adjustment}
                  amountLabel={getPriceAdjustmentSignedAmountLabel(adjustment)}
                  onHighlightAdjustments={handleHighlightAdjustments}
                  title={<CollectionSubjectLabel collections={collections} />}
                >
                  Uses{' '}
                  <strong className="font-semibold text-foreground">
                    {getPriceAdjustmentCardTitle(adjustment)}
                  </strong>{' '}
                  {formatCurrentDetailWindowPhrase(adjustment)}.
                  {fallbackContent ? <> {fallbackContent}</> : null}
                </CurrentPricingDetailRow>
              )
            })}

            <CurrentPricingDetailRow
              adjustment={activeBaseAdjustment}
              amountLabel={baseAmountLabel}
              onHighlightAdjustments={handleHighlightAdjustments}
              title={baseRowTitle}
            >
              {activeBaseAdjustment ? (
                <>
                  Uses{' '}
                  <strong className="font-semibold text-foreground">
                    {getPriceAdjustmentCardTitle(activeBaseAdjustment)}
                  </strong>{' '}
                  {formatCurrentDetailWindowPhrase(activeBaseAdjustment)}.
                  {activeBaseAdjustment?.active_window?.ends_at ? (
                    <>
                      {' '}
                      {getFallbackSentenceContent(baseFallbackAdjustment, {
                        subject: 'everything else',
                      })}
                    </>
                  ) : null}
                </>
              ) : (
                <>Uses regular product prices.</>
              )}
            </CurrentPricingDetailRow>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
